import styled, { css } from 'styled-components';
import { APP_SHELL_THEME } from '@/styles/appShellTheme';
import { BRAND } from '@/constants/brand';

/** Width of the fixed campaign-label gutter, shared by the header and rows so
 *  the month scale always lines up with the bars beneath it. */
export const LABEL_WIDTH = 300;

const SURFACE = `rgba(${APP_SHELL_THEME.overlayRgb}, 0.05)`;
const HAIRLINE = `rgba(${APP_SHELL_THEME.overlayRgb}, 0.09)`;

export const Layout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`;

/* ---------------------------------------------------------------- controls */

export const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 10px;
  padding: 8px 10px;
  background: ${APP_SHELL_THEME.insetPanelBg};
  border: 1px solid ${APP_SHELL_THEME.panelBorder};
  border-radius: 10px;
`;

export const ToolbarField = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
`;

/** Sits inline before its control, not stacked above it -- a caption row over
 *  every field made the toolbar twice as tall as the controls needed. */
export const ToolbarLabel = styled.span`
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${APP_SHELL_THEME.textMuted};
  white-space: nowrap;
`;

/** Pushes the reset action to the far end without a spacer element. */
/** Search field: takes the leftover width and gives it back under pressure,
 *  so a narrow window shrinks the input instead of wrapping the buttons. */
export const ToolbarSearch = styled(ToolbarField)`
  flex: 1 1 150px;
  min-width: 110px;

  .ant-input-group-wrapper {
    min-width: 0;
  }
`;

/* ----------------------------------------------------------------- summary */

/**
 * One quiet line instead of a row of metric cards.
 *
 * The page already carries five summary cards immediately above this tab, so a
 * second bank of big numbers repeated the same shapes and pushed the timeline
 * itself below the fold. What is left is only what the cards cannot say --
 * how much of the database the current quarter and filters are showing.
 */
export const SummaryLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 18px;
  padding: 0 2px;
  font-size: 12px;
  color: ${APP_SHELL_THEME.textMuted};
  font-variant-numeric: tabular-nums;
`;

export const SummaryFact = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;

  strong {
    color: ${APP_SHELL_THEME.text};
    font-weight: 600;
  }
`;

/** Status is never carried by colour alone: the dot travels with its label. */
export const StatusDot = styled.span<{ $colour: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $colour }) => $colour};
  flex: 0 0 auto;
`;

/* ------------------------------------------------------------------ charts */

/**
 * Three panels: flights over time, net over time, and the channel mix.
 *
 * The two time series sit side by side and the mix spans beneath them, so each
 * plot gets width enough for its own axis rather than three being squeezed
 * into one row. Panels stretch to a shared height, so the band reads as one
 * unit rather than as cards of unrelated size.
 */
export const ChartRow = styled.div`
  display: grid;
  margin-top: 2px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  align-items: stretch;

  /* The mix chart is a ranked list: full width would stretch eight short bars
     across the page, so it keeps a column and sits beside the time series on
     a wide screen. */
  @media (min-width: 1500px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const Panel = styled.div`
  padding: 10px 12px 4px;
  background: ${SURFACE};
  border: 1px solid ${APP_SHELL_THEME.metricCardBorder};
  border-radius: 10px;
  min-width: 0;
`;

/** Title on the left, the panel's headline figures on the right. */
export const PanelHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

export const PanelFacts = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

/** A stated figure beside the plot, so the two numbers worth knowing are not
 *  left to be estimated by eye off the curve. */
export const PanelFact = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.25;

  span {
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: ${APP_SHELL_THEME.textMuted};
  }

  strong {
    font-size: 12px;
    font-weight: 600;
    color: ${APP_SHELL_THEME.text};
    font-variant-numeric: tabular-nums;
  }
`;

export const PanelTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${APP_SHELL_THEME.text};
`;

export const PanelHint = styled.div`
  margin-bottom: 6px;
  font-size: 11px;
  color: ${APP_SHELL_THEME.textMuted};
`;

/**
 * Horizontal scroller for a plot with more buckets than will fit.
 *
 * A four-year range at week scale is ~200 points; drawn to the panel width
 * they collapse into a flat line with unreadable tick labels. The plot keeps a
 * minimum width per bucket and scrolls instead.
 */
export const PanelScroll = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  overscroll-behavior-x: contain;
  padding-bottom: 2px;

  /* A thin, self-effacing bar: the plot is the content, not the chrome. */
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(${APP_SHELL_THEME.overlayRgb}, 0.18);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(${APP_SHELL_THEME.overlayRgb}, 0.28);
  }
`;

/**
 * Sized by bucket count so the plot can outgrow its panel and scroll.
 *
 * `width` rather than `min-width`: recharts' ResponsiveContainer measures its
 * parent, and a parent with only a min-width reports the flex-collapsed width,
 * which left 47 monthly buckets drawn into one panel with every label
 * overlapping. A real width gives it something to measure.
 */
export const PlotCanvas = styled.div<{ $minWidth: number }>`
  /* At least the panel's width so a short range fills it, and exactly the
     computed width once the buckets need more than that. */
  width: max(100%, ${({ $minWidth }) => $minWidth}px);
`;

/** Shared tooltip surface for the recharts panels and the Gantt bars. */
export const TooltipCard = styled.div`
  padding: 10px 12px;
  background: #151a52;
  border: 1px solid ${APP_SHELL_THEME.panelBorder};
  border-radius: 8px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.35);
  font-size: 12px;
  line-height: 1.5;
  color: ${APP_SHELL_THEME.text};
`;

export const TooltipTitle = styled.div`
  font-weight: 600;
  margin-bottom: 4px;
`;

export const TooltipRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  color: ${APP_SHELL_THEME.textMuted};
  font-variant-numeric: tabular-nums;

  strong {
    color: ${APP_SHELL_THEME.text};
    font-weight: 500;
  }
`;

/** The trailing buttons stay together on the control row. */
export const ToolbarActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
`;

/* ------------------------------------------------------------------- gantt */

export const TimelineScroll = styled.div`
  /* Scrolls in both directions: down through campaigns, across through time.
     The header row and the campaign column stay pinned while it does. */
  overflow: auto;
  max-height: min(64vh, 620px);
  border: 1px solid ${APP_SHELL_THEME.panelBorder};
  border-radius: 10px;
  background: ${APP_SHELL_THEME.panelBg};
  /* Snappier than free scrolling over a wide canvas, without fighting a drag. */
  overscroll-behavior-x: contain;
`;

/**
 * The scrolling canvas, sized by how much time the axis covers.
 *
 * A fixed min-width squeezes every column into the viewport instead of
 * scrolling: a four-year range at month scale gave 19px columns whose labels
 * all truncated to "Ma", "Ju", "Oc". The width is driven by tick count so each
 * column keeps a legible minimum and the container scrolls when the range
 * outgrows the screen.
 */
export const TimelineInner = styled.div<{ $minWidth: number }>`
  min-width: ${({ $minWidth }) => $minWidth}px;
`;

export const HeaderStack = styled.div`
  position: sticky;
  top: 0;
  /* Above the sticky row labels, which are themselves above the bars. */
  z-index: 5;
  background: ${APP_SHELL_THEME.tableFixedHeaderBg};
  border-bottom: 1px solid ${APP_SHELL_THEME.panelBorder};
`;

export const HeaderRow = styled.div`
  display: flex;
`;

/** Holds the weighted tick cells beside the fixed gutter.
 *
 *  The cells are percentages, and a percentage of a flex row that also
 *  contains a 260px gutter overflows that row by exactly the gutter width --
 *  which pushed the last column of every quarter off the right edge. Giving
 *  them their own `flex: 1 1 0` track makes 100% mean "the space left over",
 *  matching the bar tracks in the rows below. */
export const HeaderTrack = styled.div`
  display: flex;
  flex: 1 1 0;
  min-width: 0;
`;

export const HeaderGutter = styled.div<{ $muted?: boolean }>`
  flex: 0 0 ${LABEL_WIDTH}px;
  position: sticky;
  left: 0;
  /* The corner where the two frozen axes cross, so it sits above both. */
  z-index: 6;
  background: ${APP_SHELL_THEME.tableFixedHeaderBg};
  border-right: 1px solid ${APP_SHELL_THEME.panelBorder};
  padding: ${({ $muted }) => ($muted ? '2px 16px 8px' : '10px 16px 4px')};
  font-size: 10px;
  font-weight: ${({ $muted }) => ($muted ? 400 : 600)};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ $muted }) => ($muted ? APP_SHELL_THEME.textMuted : APP_SHELL_THEME.text)};
`;

/** Quarter (or month, when zoomed to weeks) band above the finer ticks. */
export const MajorCell = styled.div<{ $weight: number }>`
  flex: 0 0 ${({ $weight }) => $weight * 100}%;
  padding: 10px 8px 4px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${APP_SHELL_THEME.text};
  border-left: 1px solid ${APP_SHELL_THEME.panelBorder};
  white-space: nowrap;
  overflow: hidden;
`;

export const TickCell = styled.div<{ $weight: number; $major: boolean }>`
  flex: 0 0 ${({ $weight }) => $weight * 100}%;
  padding: 0 4px 8px;
  font-size: 11px;
  color: ${({ $major }) => ($major ? APP_SHELL_THEME.text : APP_SHELL_THEME.textMuted)};
  border-left: 1px solid ${HAIRLINE};
  white-space: nowrap;
  /* Clipped rather than ellipsised: a month abbreviation cut to "Ma…" is
     noise, and the canvas is now wide enough that clipping should not happen.
     If a column is still too narrow the label simply goes, which reads better
     than a truncation mark. */
  overflow: hidden;
  text-align: center;
  min-width: 0;
`;

export const TimelineRow = styled.div`
  display: flex;
  align-items: center;
  border-bottom: 1px solid ${HAIRLINE};
  transition: background 120ms ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(${APP_SHELL_THEME.overlayRgb}, 0.05);
  }
`;

export const RowLabel = styled.div`
  flex: 0 0 ${LABEL_WIDTH}px;
  position: sticky;
  left: 0;
  /* Above the bars (1) and the today marker (2): the frozen column has to
     cover whatever scrolls beneath it, and a wide bar was painting straight
     across the campaign names. Still below the header (3) and its corner (4). */
  z-index: 3;
  /* Opaque: the bars scroll underneath this column, so a tinted background
     would let them show through the campaign names. */
  background: ${APP_SHELL_THEME.panelBg};
  /* Marks where the frozen column ends and the scrolling axis begins. */
  border-right: 1px solid ${APP_SHELL_THEME.panelBorder};
  padding: 8px 16px;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 10px;

  ${TimelineRow}:hover & {
    background: #101c7d;
  }
`;

/** Channel colour repeated beside the name, so the label and its bar are tied
 *  together without relying on horizontal tracking across a wide chart. */
export const ChannelDot = styled.span<{ $colour: string }>`
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $colour }) => $colour};
`;

export const RowText = styled.div`
  min-width: 0;
  flex: 1 1 auto;
`;

/** Channel dot and status pip stacked at the row start, so both read as
 *  properties of the campaign rather than floating in the gutter. */
export const RowMarks = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

export const RowCampaign = styled.div`
  font-size: 13px;
  color: ${APP_SHELL_THEME.text};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const RowMeta = styled.div`
  font-size: 11px;
  color: ${APP_SHELL_THEME.textMuted};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const BarTrack = styled.div`
  position: relative;
  flex: 1 1 0;
  height: 36px;
`;

export const Grid = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  pointer-events: none;
`;

export const GridCell = styled.div<{ $weight: number; $major: boolean; $alt: boolean }>`
  flex: 0 0 ${({ $weight }) => $weight * 100}%;
  border-left: 1px solid ${({ $major }) => ($major ? `rgba(${APP_SHELL_THEME.overlayRgb}, 0.14)` : HAIRLINE)};
  /* Alternating wash gives the eye a column to land on without adding rules. */
  background: ${({ $alt }) => ($alt ? `rgba(${APP_SHELL_THEME.overlayRgb}, 0.022)` : 'transparent')};

  &:first-child {
    border-left: none;
  }
`;

/** Today, drawn across the whole chart so live campaigns are obvious. */
export const TodayMarker = styled.div<{ $left: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${({ $left }) => $left}%;
  width: 0;
  border-left: 1px solid ${BRAND.hacienda};
  opacity: 0.85;
  pointer-events: none;
  z-index: 2;
`;

export const TodayFlag = styled.div<{ $left: number }>`
  position: absolute;
  top: 2px;
  left: ${({ $left }) => $left}%;
  transform: translateX(-50%);
  padding: 1px 6px;
  border-radius: 999px;
  background: ${BRAND.hacienda};
  color: ${BRAND.ink};
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.06em;
  white-space: nowrap;
  z-index: 4;
  pointer-events: none;
`;

export const Bar = styled.div<{
  $left: number;
  $width: number;
  $colour: string;
  $dim: boolean;
  $clipStart: boolean;
  $clipEnd: boolean;
  $unpriced: boolean;
}>`
  position: absolute;
  height: ${({ $unpriced }) => ($unpriced ? 10 : 16)}px;
  top: ${({ $unpriced }) => ($unpriced ? 13 : 10)}px;
  left: ${({ $left }) => $left}%;
  width: ${({ $width }) => $width}%;
  min-width: 3px;
  /* A flight carrying no spend -- measurement, consultancy and other non-media
     lines -- is drawn as an outline rather than a solid bar. Filled, it had
     exactly the visual weight of a six-figure campaign while representing no
     money, which made the chart's loudest rows its least significant. */
  background: ${({ $colour, $unpriced }) =>
    $unpriced ? `repeating-linear-gradient(
      135deg,
      ${$colour}55 0 5px,
      transparent 5px 10px
    )` : $colour};
  /* Priced bars carry a ring in the surface colour so two that touch stay
     separate; unpriced ones carry their outline on the inside instead. */
  box-shadow: ${({ $unpriced, $colour }) =>
    $unpriced
      ? `inset 0 0 0 1px ${$colour}99`
      : `0 0 0 1.5px ${APP_SHELL_THEME.panelBg}`};
  /* 4px rounded data-ends; a clipped end stays square so "runs on past here"
     is legible rather than looking like the flight simply finished. */
  border-radius: ${({ $clipStart, $clipEnd }) =>
    `${$clipStart ? 0 : 4}px ${$clipEnd ? 0 : 4}px ${$clipEnd ? 0 : 4}px ${$clipStart ? 0 : 4}px`};
  /* Ended flights step back, but by desaturating toward grey rather than by
     opacity -- fading into the blue panel shifted every hue and made an orange
     bar read as maroon, breaking the tie to its legend colour. */
  filter: ${({ $dim }) => ($dim ? 'saturate(0.7) brightness(0.88)' : 'none')};
  transition: filter 120ms ease;
  cursor: default;
  z-index: 1;

  &:hover {
    filter: brightness(1.18);
  }
`;

/** Net printed just past the bar when there is room for it -- selectively, not
 *  a number on every row. */
export const BarValue = styled.span<{ $left: number; $flip: boolean }>`
  position: absolute;
  top: 12px;
  ${({ $flip, $left }) =>
    $flip
      ? css`
          right: ${100 - $left}%;
          padding-right: 8px;
        `
      : css`
          left: ${$left}%;
          padding-left: 8px;
        `}
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  color: ${APP_SHELL_THEME.textMuted};
  white-space: nowrap;
  pointer-events: none;
`;

/* ------------------------------------------------------------------ legend */

export const LegendRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 14px;
  padding: 10px 16px;
`;

export const LegendItem = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 8px;
  border: 1px solid ${({ $active }) => ($active ? APP_SHELL_THEME.accent : 'transparent')};
  border-radius: 999px;
  background: ${({ $active }) => ($active ? 'rgba(97, 97, 215, 0.22)' : 'transparent')};
  font-size: 11px;
  color: ${({ $active }) => ($active ? APP_SHELL_THEME.text : APP_SHELL_THEME.textMuted)};
  cursor: pointer;
  transition: background 120ms ease, color 120ms ease;

  &:hover {
    color: ${APP_SHELL_THEME.text};
    background: rgba(${APP_SHELL_THEME.overlayRgb}, 0.07);
  }

  &:focus-visible {
    outline: 2px solid ${APP_SHELL_THEME.accent};
    outline-offset: 1px;
  }
`;

export const FooterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px 16px;
  padding: 4px 2px;
  font-size: 11px;
  color: ${APP_SHELL_THEME.textMuted};
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 44px;
  text-align: center;
  color: ${APP_SHELL_THEME.textMuted};
  border: 1px solid ${APP_SHELL_THEME.panelBorder};
  border-radius: 10px;
  background: ${APP_SHELL_THEME.panelBg};
`;
